import { useState } from "react";
import { Navigate } from "react-router-dom";
import { CalendarRange, Images, Inbox, LogOut, Settings, ShoppingBag, User, Users, LayoutGrid } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { brl } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Logo } from "@/components/site/Logo";
import { PanelEvents } from "@/components/panel/PanelEvents";
import { PanelOrders } from "@/components/panel/PanelOrders";
import { PanelLeads } from "@/components/panel/PanelLeads";
import { PanelProfile } from "@/components/panel/PanelProfile";
import { PanelTeam } from "@/components/panel/PanelTeam";
import { PanelSettings } from "@/components/panel/PanelSettings";

const Overview = ({ go }) => {
  const { data } = useFetch("/panel/overview", {});
  const d = data || {};
  const cards = [
    ["events", "Eventos ativos", d.events ?? 0, CalendarRange],
    ["orders", "Pedidos aguardando", d.orders_pending ?? 0, ShoppingBag],
    ["leads", "Solicitações novas", d.leads_new ?? 0, Inbox],
    ["orders", "Faturado (pago)", brl(d.revenue_cents), Images],
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="panel-overview">
      {cards.map(([tab, label, value, Icon], i) => (
        <button key={label} onClick={() => go(tab)} data-testid={`overview-card-${i}`} className="rounded-[1.5rem] border border-white/10 bg-ink-2 p-6 text-left transition-colors hover:border-flash/40">
          <Icon className="h-6 w-6 text-flash" />
          <p className="mt-6 font-display text-4xl font-bold">{value}</p>
          <p className="mt-1 text-sm text-haze">{label}</p>
        </button>
      ))}
      <p className="text-sm text-haze sm:col-span-2 xl:col-span-4">Fotos publicadas: {d.photos ?? 0}</p>
    </div>
  );
};

export default function Panel() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  if (user === null) return <main className="grid min-h-screen place-items-center text-haze">Carregando…</main>;
  if (!user) return <Navigate to="/entrar" replace />;

  const isAdmin = user.role === "admin";
  const tabs = [
    ["overview", "Visão geral", LayoutGrid], ["events", "Eventos & fotos", CalendarRange], ["orders", "Pedidos", ShoppingBag],
    ["leads", "Solicitações", Inbox], ["profile", "Meu perfil", User],
    ...(isAdmin ? [["team", "Fotógrafos", Users], ["settings", "Configurações", Settings]] : []),
  ];
  const views = {
    overview: <Overview go={setTab} />, events: <PanelEvents user={user} />, orders: <PanelOrders />, leads: <PanelLeads user={user} />,
    profile: <PanelProfile user={user} />, team: <PanelTeam />, settings: <PanelSettings />,
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr]" data-testid="panel-page">
      <aside className="sticky top-0 z-30 border-b border-white/10 bg-ink/90 backdrop-blur-xl lg:h-screen lg:border-b-0 lg:border-r">
        <div className="flex items-center justify-between px-5 py-5 lg:block">
          <Logo />
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-haze hover:text-flash lg:hidden" data-testid="panel-logout-mobile"><LogOut className="h-4 w-4" />Sair</button>
        </div>
        <nav className="scrollbar-none flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:px-3">
          {tabs.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`panel-tab-${k}`}
              className={`flex shrink-0 items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-colors ${tab === k ? "bg-flash text-ink font-semibold" : "text-foreground/80 hover:bg-white/5"}`}>
              <Icon className="h-4 w-4" />{l}
            </button>
          ))}
        </nav>
        <div className="absolute bottom-6 left-5 right-5 hidden lg:block">
          <p className="truncate text-sm font-semibold">{user.name}</p>
          <p className="truncate text-xs text-haze">{user.email} · {isAdmin ? "Administrador" : "Fotógrafo"}</p>
          <button onClick={logout} className="mt-3 inline-flex items-center gap-2 text-sm text-haze hover:text-flash" data-testid="panel-logout-btn"><LogOut className="h-4 w-4" />Sair</button>
        </div>
      </aside>
      <main className="px-5 py-8 sm:px-10 lg:py-12">
        <h1 className="mb-8 font-display text-3xl font-bold sm:text-4xl" data-testid="panel-title">{tabs.find((t) => t[0] === tab)?.[1]}</h1>
        {views[tab]}
      </main>
    </div>
  );
}
